/**
 * Attorney Analysis Section for Accountant Report View
 * AI-powered case builder from the ATTORNEY/CLAIMANT perspective
 * 
 * Purpose: Help plaintiff attorneys maximize client recovery by:
 * - Identifying unclaimed losses ("money left on the table")
 * - Finding critical vulnerabilities that need fixing
 * - Building stronger documentation
 * - Providing actionable case strategy
 * 
 * Based on the Attorney Case Builder methodology:
 * - Maximum Loss Calculation mindset
 * - Gap Analysis & Solutions
 * - Proactive Defense Preparation
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Scale,
  TrendingUp, 
  DollarSign, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  FileText,
  Target,
  Clock,
  Users,
  Briefcase
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface AttorneyAnalysisProps {
  reportId: string;
  getAuthHeader: () => { Authorization: string };
  formatCurrency: (amount?: number) => string;
}

interface UnclaimedLoss {
  category: string;
  title: string;
  description: string;
  dollar_impact: number;
  why_recoverable: string;
  confidence: string;
  attorney_tasks?: string[];
  client_tasks?: string[];
  evidence_needed?: string[];
  insurance_response?: string;
  counter_argument?: string;
}

interface CriticalVulnerability {
  issue_number: number;
  title: string;
  severity: string;
  the_problem: string;
  why_it_matters: string;
  financial_risk: number;
  the_fix: {
    steps: string[];
    deadline: string;
    required_docs: string[];
  };
  fallback_position?: string;
}

interface SettlementStrategy {
  current_claim: number;
  potential_claim: number;
  additional_recovery: number;
  claim_strength: string;
  opening_demand: number;
  target_settlement: number;
  walk_away: number;
  leverage_points: string[];
  expected_insurance_response: string;
}

interface AttorneyAnalysis {
  success: boolean;
  cached: boolean;
  analysis_id?: string;
  analyzed_at?: string;
  
  // Executive Summary
  claim_amount?: number;
  potential_claim?: number;
  additional_recovery?: number;
  executive_summary?: string;
  case_strength?: string;
  settlement_probability?: string;
  
  // Unclaimed Losses
  unclaimed_losses?: UnclaimedLoss[];
  total_unclaimed?: number;
  
  // Critical Vulnerabilities
  vulnerabilities?: CriticalVulnerability[];
  total_risk?: number;
  
  // Settlement Strategy
  settlement_strategy?: SettlementStrategy;
  
  // Client Action Plan
  client_actions?: {
    immediate: string[];
    this_week: string[];
    next_week: string[];
  };
  
  // Recommendations
  recommendations?: string[];
  
  error?: string;
}

export default function AttorneyAnalysisSection({
  reportId,
  getAuthHeader,
  formatCurrency
}: AttorneyAnalysisProps) {
  const [analysis, setAnalysis] = useState<AttorneyAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    unclaimed: true,
    vulnerabilities: true,
    strategy: false,
    actions: false
  });

  const fetchAnalysis = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/attorney/analyze/${reportId}?force_refresh=${forceRefresh}`,
        {},
        { headers: getAuthHeader() }
      );
      setAnalysis(response.data);
    } catch (err: any) {
      console.error('Attorney analysis failed:', err);
      setError(err.response?.data?.detail || 'Failed to load attorney analysis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalysis();
  }, [reportId]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getCaseStrengthColor = (strength?: string) => {
    switch (strength?.toUpperCase()) {
      case 'STRONG': return 'bg-green-100 text-green-800 border-green-300';
      case 'MODERATE': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'WEAK': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'AT_RISK': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getSeverityColor = (severity?: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'bg-red-50 border-red-300 text-red-900';
      case 'high': return 'bg-orange-50 border-orange-300 text-orange-900';
      case 'medium': return 'bg-amber-50 border-amber-300 text-amber-900';
      case 'low': return 'bg-blue-50 border-blue-300 text-blue-900';
      default: return 'bg-gray-50 border-gray-300 text-gray-900';
    }
  };

  const getConfidenceColor = (confidence?: string) => {
    switch (confidence?.toLowerCase()) {
      case 'high': return 'text-green-600';
      case 'medium': return 'text-amber-600';
      case 'low': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (loading && !analysis) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-emerald-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Scale className="w-6 h-6 text-emerald-600" />
          <h2 className="text-xl font-bold text-gray-900">Attorney Analysis</h2>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mb-4"></div>
            <p className="text-gray-600">Building your case...</p>
            <p className="text-sm text-gray-500 mt-1">Analyzing for maximum recovery</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-emerald-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Scale className="w-6 h-6 text-emerald-600" />
          <h2 className="text-xl font-bold text-gray-900">Attorney Analysis</h2>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => fetchAnalysis(true)}
            className="mt-3 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm"
          >
            Retry Analysis
          </button>
        </div>
      </div>
    );
  }

  if (!analysis) return null;

  const { 
    executive_summary,
    case_strength,
    settlement_probability,
    claim_amount,
    potential_claim,
    additional_recovery,
    unclaimed_losses,
    total_unclaimed,
    vulnerabilities,
    total_risk,
    settlement_strategy,
    client_actions,
    recommendations,
    cached,
    analyzed_at
  } = analysis;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-emerald-200 p-6 mb-6" data-testid="attorney-analysis-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Scale className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Attorney Analysis</h2>
            <p className="text-sm text-gray-500">
              {cached ? '(Cached)' : 'Fresh analysis'} • {analyzed_at ? new Date(analyzed_at).toLocaleString() : ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => fetchAnalysis(true)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Re-analyze
        </button>
      </div>

      {/* Executive Summary */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 mb-6 border border-emerald-100">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Case Analysis Summary</h3>
            <p className="text-gray-700">{executive_summary}</p>
          </div>
          <div className="flex flex-col items-end gap-2 ml-4">
            <span className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${getCaseStrengthColor(case_strength)}`}>
              {case_strength?.replace('_', ' ')} CASE
            </span>
            {settlement_probability && (
              <span className="text-sm text-gray-600">
                {settlement_probability} settlement probability
              </span>
            )}
          </div>
        </div>

        {/* Financial Summary */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-emerald-200">
          <div className="text-center">
            <p className="text-sm text-gray-600">Current Claim</p>
            <p className="text-lg font-bold text-gray-700">
              {formatCurrency(claim_amount)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Potential Claim</p>
            <p className="text-lg font-bold text-emerald-600">
              {formatCurrency(potential_claim)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Additional Recovery</p>
            <p className="text-lg font-bold text-green-600">
              +{formatCurrency(additional_recovery)}
            </p>
          </div>
        </div>
      </div>

      {/* Unclaimed Losses - Money on the Table */}
      {unclaimed_losses && unclaimed_losses.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('unclaimed')}
            className="w-full flex items-center justify-between p-4 bg-green-50 rounded-lg hover:bg-green-100 transition"
          >
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-green-900">
                Unclaimed Losses: {formatCurrency(total_unclaimed)} on the Table
              </span>
            </div>
            {expandedSections.unclaimed ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {expandedSections.unclaimed && (
            <div className="mt-3 space-y-4">
              {unclaimed_losses.map((loss, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-lg bg-green-50 border border-green-200"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      <TrendingUp className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-green-900">{loss.title}</h4>
                        <span className="text-xs px-2 py-0.5 bg-green-200 text-green-800 rounded">{loss.category}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-700">+{formatCurrency(loss.dollar_impact)}</p>
                      <span className={`text-xs font-semibold ${getConfidenceColor(loss.confidence)}`}>
                        {loss.confidence} confidence
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-700 mb-3">{loss.description}</p>
                  
                  <div className="bg-white rounded p-3 mb-3">
                    <p className="text-sm font-semibold text-green-700 mb-1">Why This is Recoverable:</p>
                    <p className="text-sm text-gray-600">{loss.why_recoverable}</p>
                  </div>

                  {loss.attorney_tasks && loss.attorney_tasks.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs font-semibold text-gray-600 mb-1">Attorney Tasks:</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {loss.attorney_tasks.map((task, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-emerald-500">→</span> {task}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {loss.client_tasks && loss.client_tasks.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs font-semibold text-gray-600 mb-1">Client Tasks:</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {loss.client_tasks.map((task, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-blue-500">☐</span> {task}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {loss.insurance_response && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <p className="text-xs">
                        <span className="font-semibold text-red-600">Expected Insurance Response:</span>{' '}
                        <span className="text-gray-600">{loss.insurance_response}</span>
                      </p>
                      {loss.counter_argument && (
                        <p className="text-xs mt-1">
                          <span className="font-semibold text-emerald-600">Counter-Argument:</span>{' '}
                          <span className="text-gray-600">{loss.counter_argument}</span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Critical Vulnerabilities - Fix These Now */}
      {vulnerabilities && vulnerabilities.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('vulnerabilities')}
            className="w-full flex items-center justify-between p-4 bg-red-50 rounded-lg hover:bg-red-100 transition"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="font-semibold text-red-900">
                Critical Vulnerabilities ({vulnerabilities.length}) - Fix These Now
              </span>
            </div>
            {expandedSections.vulnerabilities ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {expandedSections.vulnerabilities && (
            <div className="mt-3 space-y-4">
              {vulnerabilities.map((vuln, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border-l-4 ${getSeverityColor(vuln.severity)}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold">Critical Issue #{vuln.issue_number}: {vuln.title}</h4>
                        <span className="text-xs px-2 py-0.5 bg-red-200 text-red-800 rounded uppercase">{vuln.severity}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-red-700">Risk: {formatCurrency(vuln.financial_risk)}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="bg-white/50 rounded p-3">
                      <p className="text-sm font-semibold text-red-700">The Problem:</p>
                      <p className="text-sm text-gray-700">{vuln.the_problem}</p>
                    </div>

                    <div className="bg-white/50 rounded p-3">
                      <p className="text-sm font-semibold text-amber-700">Why This Matters:</p>
                      <p className="text-sm text-gray-700">{vuln.why_it_matters}</p>
                    </div>

                    <div className="bg-emerald-50 rounded p-3 border border-emerald-200">
                      <p className="text-sm font-semibold text-emerald-700 mb-2">THE FIX:</p>
                      <ul className="text-sm text-gray-700 space-y-1 mb-2">
                        {vuln.the_fix.steps.map((step, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-emerald-500 font-bold">{i + 1}.</span> {step}
                          </li>
                        ))}
                      </ul>
                      <div className="flex items-center gap-4 text-xs text-gray-600 mt-2 pt-2 border-t border-emerald-200">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Deadline: {vuln.the_fix.deadline}
                        </span>
                      </div>
                      {vuln.the_fix.required_docs.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs font-semibold text-gray-600">Required Documentation:</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {vuln.the_fix.required_docs.map((doc, i) => (
                              <span key={i} className="text-xs px-2 py-0.5 bg-white text-gray-600 rounded">{doc}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {vuln.fallback_position && (
                      <div className="bg-amber-50 rounded p-3 border border-amber-200">
                        <p className="text-xs font-semibold text-amber-700">Fallback Position:</p>
                        <p className="text-xs text-gray-600">{vuln.fallback_position}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Settlement Strategy */}
      {settlement_strategy && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('strategy')}
            className="w-full flex items-center justify-between p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
          >
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-blue-900">Settlement Strategy</span>
            </div>
            {expandedSections.strategy ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {expandedSections.strategy && (
            <div className="mt-3 bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Opening Demand</p>
                  <p className="text-lg font-bold text-blue-700">{formatCurrency(settlement_strategy.opening_demand)}</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Target Settlement</p>
                  <p className="text-lg font-bold text-emerald-700">{formatCurrency(settlement_strategy.target_settlement)}</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Walk Away</p>
                  <p className="text-lg font-bold text-red-700">{formatCurrency(settlement_strategy.walk_away)}</p>
                </div>
              </div>

              {settlement_strategy.leverage_points && settlement_strategy.leverage_points.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Leverage Points:</p>
                  <ul className="space-y-1">
                    {settlement_strategy.leverage_points.map((point, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                        <span className="text-blue-500">✓</span> {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {settlement_strategy.expected_insurance_response && (
                <div className="bg-white rounded p-3">
                  <p className="text-sm font-semibold text-gray-700 mb-1">Expected Insurance Response:</p>
                  <p className="text-sm text-gray-600">{settlement_strategy.expected_insurance_response}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Client Action Plan */}
      {client_actions && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('actions')}
            className="w-full flex items-center justify-between p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition"
          >
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-purple-600" />
              <span className="font-semibold text-purple-900">Client Action Plan</span>
            </div>
            {expandedSections.actions ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {expandedSections.actions && (
            <div className="mt-3 bg-purple-50 rounded-lg p-4 border border-purple-200">
              <div className="grid grid-cols-3 gap-4">
                {client_actions.immediate && client_actions.immediate.length > 0 && (
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-sm font-bold text-red-600 mb-2">⚡ Immediate (24-48hrs)</p>
                    <ul className="space-y-1">
                      {client_actions.immediate.map((action, idx) => (
                        <li key={idx} className="text-xs text-gray-600 flex items-start gap-1">
                          <span className="text-red-400">☐</span> {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {client_actions.this_week && client_actions.this_week.length > 0 && (
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-sm font-bold text-amber-600 mb-2">📅 This Week</p>
                    <ul className="space-y-1">
                      {client_actions.this_week.map((action, idx) => (
                        <li key={idx} className="text-xs text-gray-600 flex items-start gap-1">
                          <span className="text-amber-400">☐</span> {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {client_actions.next_week && client_actions.next_week.length > 0 && (
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-sm font-bold text-blue-600 mb-2">📆 Next Week</p>
                    <ul className="space-y-1">
                      {client_actions.next_week.map((action, idx) => (
                        <li key={idx} className="text-xs text-gray-600 flex items-start gap-1">
                          <span className="text-blue-400">☐</span> {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <h3 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            Case Strategy Recommendations
          </h3>
          <ul className="space-y-2">
            {recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-emerald-800">
                <span className="text-emerald-500 mt-1">→</span>
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
